import React, { useCallback, useEffect, useState } from "react";
import Title_Section from "../../../components/Title_Section/Title_Section";
import Button from "../../../components/Button";
import { Grid } from "@mui/material";
import AddIcon from "@material-ui/icons/Add";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import { ROUTE_PATHS } from "../../../utils/PathsNames";
import FlashCardList from "./FlashCardList";
import SmartImportModal from "./SmartImportModal";
import { useDispatch, useSelector } from "react-redux";
import EmptyState from "../../../components/EmptyState";
import DeleteModal from "../../../components/DeleteModal";
import { useIntl } from "react-intl";
import { Form, Formik } from "formik";
import Search from "../../../components/Search";
import Pagination from "../../../components/Pagination";
import {
  deleteSingleFlashCardRequest,
  getAllFlashCardRequest,
  setFlashcardtSearch,
  setFlashcardtsPage
} from "../../../store/FlashCard/Questions/actions";
import CustomSelect from "../../../components/Select";
import InfiniteScrollSelect from "../../../components/Select/InfiniteScrollSelect";
import { formatOptions } from "../../../utils/Helpers";
import { DIFFIECULTY_LEVEL_LIST } from "../../../utils/Constants";
import {
  getAllFlashCardSubTopicsRequest,
  getAllFlashCardTopicsRequest
} from "../../../store/FlashCard/Topics/actions";

const INITIAL_FILTERS = {
  difficulty_level: "",
  topic_id: "",
  sub_topic_id: ""
};

function FlashCardQuestions() {
  const isLoading = useSelector((state) => state.loader);
  const { list, searchKeyword, flashcardsPage, meta } = useSelector(
    (state) => state.flashCardQuestions
  );
  const {
    list: topicsList,
    hasMore: hasMoreTopics,
    subTopicsList,
    subTopicsHasMore
  } = useSelector((state) => state.flashCardTopics);
  const lang = useSelector((state) => state.locale.lang);
  const dispatch = useDispatch();
  const perPage = 10;

  // ─── Topics query state ─────────────────────────────────────────
  const [topicsQuery, setTopicsQuery] = useState({
    page: 1,
    name: "",
    items: perPage,
    paginate: true
  });

  useEffect(() => {
    dispatch(getAllFlashCardTopicsRequest(topicsQuery));
  }, [topicsQuery, dispatch]);

  const handleTopicSearch = useCallback((inputValue) => {
    setTopicsQuery((prev) => ({
      ...prev,
      page: 1,
      name: inputValue
    }));
  }, []);

  const handleTopicLoadMore = useCallback(() => {
    setTopicsQuery((prev) => ({ ...prev, page: prev.page + 1 }));
  }, []);

  // ─── Sub-topics query state ──────────────────────────────────────
  const [subTopicsQuery, setSubTopicsQuery] = useState(null);

  useEffect(() => {
    if (!subTopicsQuery?.topicId) return;
    dispatch(getAllFlashCardSubTopicsRequest(subTopicsQuery));
  }, [subTopicsQuery, dispatch]);

  const handleSubTopicSearch = useCallback((inputValue) => {
    setSubTopicsQuery((prev) =>
      prev ? { ...prev, page: 1, name: inputValue } : prev
    );
  }, []);

  const handleSubTopicLoadMore = useCallback(() => {
    setSubTopicsQuery((prev) =>
      prev ? { ...prev, page: prev.page + 1 } : prev
    );
  }, []);

  // ─── Flashcards filters ──────────────────────────────────────────
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [isSmartImportOpen, setIsSmartImportOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    id: 0
  });

  const { messages } = useIntl();
  const {
    flashcard: { deleteMessage, deleteFlashCard }
  } = messages;

  useEffect(() => {
    dispatch(
      getAllFlashCardRequest({
        page: flashcardsPage,
        items: perPage,
        paginate: true,
        question_code: searchKeyword || "",
        ...(filters.difficulty_level && {
          difficulty_level: filters.difficulty_level
        }),
        ...(filters.topic_id && { topic_id: filters.topic_id }),
        ...(filters.sub_topic_id && {
          sub_topic_id: filters.sub_topic_id
        })
      })
    );
  }, [flashcardsPage, searchKeyword, filters]);

  useEffect(() => {
    // Add a cleanup return that resets the Redux values if the component unmounts
    return () => {
      dispatch(setFlashcardtSearch(""));
      dispatch(setFlashcardtsPage(1));
    };
  }, [dispatch]);

  const handleSearch = (e) => {
    dispatch(setFlashcardtSearch(e.target.value));
    dispatch(setFlashcardtsPage(1));
  };

  const handlePagination = (_, newPage) => {
    dispatch(setFlashcardtsPage(newPage));
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    dispatch(setFlashcardtsPage(1));
  };

  const handleDelete = () => {
    dispatch(deleteSingleFlashCardRequest({ id: deleteModal.id }));
    setDeleteModal({ open: false, id: 0 });
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <Title_Section title={"Questions"} />
        <div className="d-flex align-items-center">
          <div className="me-2">
            <Button
              text={"Smart Import"}
              icon={<CloudUploadIcon className="me-2" />}
              className="primary-light"
              onClick={() => setIsSmartImportOpen(true)}
            />
          </div>
          <Button
            text={"Add Question"}
            icon={<AddIcon className="me-2" />}
            isLink={true}
            slug={`${ROUTE_PATHS.flashcardQuestions}/Add`}
          />
        </div>
      </div>

      <div>
        <Formik
          initialValues={{ search: "", ...INITIAL_FILTERS }}
          onSubmit={() => {}}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid
                container
                spacing={2}
                className="mb-3"
                alignItems="center"
              >
                {/* Search */}
                <Grid item xs={6} minWidth={"200px"} md={3}>
                  <Search
                    name="search"
                    placeholder="Search by question code"
                    onChange={handleSearch}
                  />
                </Grid>

                {/* Difficulty Level */}
                <Grid item xs={12} sm={6} md={2}>
                  <CustomSelect
                    isAddNoneOption={true}
                    name="difficulty_level"
                    options={DIFFIECULTY_LEVEL_LIST.map((item) =>
                      formatOptions(item, "id", "name")
                    )}
                    bgColor="#ecf1fb"
                    placeholder="Difficulty"
                    isRequired={false}
                    onChange={(selected) => {
                      setFieldValue(
                        "difficulty_level",
                        selected?.id ?? ""
                      );
                      handleFilterChange(
                        "difficulty_level",
                        selected?.id ?? ""
                      );
                    }}
                  />
                </Grid>

                {/* Topic */}
                <Grid item xs={12} sm={6} md={2}>
                  <InfiniteScrollSelect
                    name="topic_id"
                    placeholder="System"
                    bgColor="#ecf1fb"
                    isAddNoneOption={true}
                    isRequired={false}
                    items={topicsList ?? []}
                    hasMore={hasMoreTopics}
                    onSearch={handleTopicSearch}
                    onLoadMore={handleTopicLoadMore}
                    onChange={(topicId) => {
                      handleFilterChange("sub_topic_id", "");
                      handleFilterChange("topic_id", topicId);

                      // Kick off sub-topics fetch only when a topic is chosen
                      if (topicId) {
                        setSubTopicsQuery({
                          page: 1,
                          name: "",
                          items: perPage,
                          paginate: true,
                          topicId
                        });
                      } else {
                        setSubTopicsQuery(null);
                      }
                    }}
                  />
                </Grid>

                {/* Sub-Topic */}
                <Grid item xs={12} sm={6} md={2}>
                  <InfiniteScrollSelect
                    name="sub_topic_id"
                    placeholder="Subject"
                    bgColor={!values.topic_id ? "#f5f5f5" : "#ecf1fb"}
                    isAddNoneOption={true}
                    isRequired={false}
                    disabled={!values.topic_id}
                    items={subTopicsList ?? []}
                    hasMore={subTopicsHasMore}
                    onSearch={handleSubTopicSearch}
                    onLoadMore={handleSubTopicLoadMore}
                    onChange={(subTopicId) => {
                      handleFilterChange("sub_topic_id", subTopicId);
                    }}
                  />
                </Grid>
              </Grid>
              <button className="d-none" />
            </Form>
          )}
        </Formik>

        <FlashCardList list={list} setDeleteModal={setDeleteModal} />
        {!isLoading && !list?.length && <EmptyState />}

        {meta?.pages > 1 && (
          <Pagination
            count={meta.pages}
            page={meta.currentPage || flashcardsPage}
            handleChange={handlePagination}
          />
        )}

        <DeleteModal
          deleteMessage={deleteMessage}
          deleteTitle={deleteFlashCard}
          isOpen={deleteModal.open}
          lang={lang}
          handleDelete={handleDelete}
          handleCancel={() => setDeleteModal({ id: 0, open: false })}
        />

        <SmartImportModal
          isOpen={isSmartImportOpen}
          handleClose={() => setIsSmartImportOpen(false)}
        />
      </div>
    </>
  );
}

export default FlashCardQuestions;
